/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { CollectionPlp } from '@theme/collection-plp-component';
import { querySearch } from '@theme/search-plp-api';

const SEARCH_ORIGIN_PARAM = 'elevate-origin';
const CORRECTED_FROM_PARAM = 'elevate-corrected-from';
const CORRECTED_FROM_HITS_PARAM = 'elevate-corrected-from-hits';
const FACET_PREFIX = 'f.';

class SearchPlp extends CollectionPlp {
  constructor() {
    super();
    this.handleSearchSubmit = this.handleSearchSubmit.bind(this);
  }

  get query() {
    return new URL(window.location.href).searchParams.get('q') ?? '';
  }

  get searchOrigin() {
    return new URL(window.location.href).searchParams.get(SEARCH_ORIGIN_PARAM) || 'ORGANIC';
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('submit', this.handleSearchSubmit);
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.handleSearchSubmit);
    super.disconnectedCallback();
  }

  queryListing(options) {
    return querySearch({
      ...options,
      query: this.query,
      origin: this.searchOrigin,
    });
  }

  handleSearchSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.search-page-input__parent')) return;

    const input = form.elements.namedItem('q');
    if (!(input instanceof HTMLInputElement)) return;

    event.preventDefault();
    this.navigateToQuery(input.value, 'ORGANIC');
  }

  getSearchURL(query, origin = 'ORGANIC') {
    const url = new URL(window.location.href);

    url.searchParams.set('q', query.trim());
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith(FACET_PREFIX)) url.searchParams.delete(key);
    }
    url.searchParams.delete('sort');
    url.searchParams.delete('page');
    url.searchParams.delete(CORRECTED_FROM_PARAM);
    url.searchParams.delete(CORRECTED_FROM_HITS_PARAM);

    if (origin === 'ORGANIC') url.searchParams.delete(SEARCH_ORIGIN_PARAM);
    else url.searchParams.set(SEARCH_ORIGIN_PARAM, origin);

    return url;
  }

  navigateToQuery(query, origin) {
    const url = this.getSearchURL(query, origin);
    history.pushState({}, '', url);
    this.syncSearchInput();
    this.renderSkeleton();
    this.refresh().catch((error) => {
      this.setCollectionHeaderCountLoading(false);
      this.renderError();
      console.error('[Elevate Search] Failed to update the query', error);
    });
  }

  syncSearchInput() {
    const input = document.querySelector('.search-page-input__parent input[name="q"]');
    if (input instanceof HTMLInputElement) input.value = this.query;
  }

  createSearchLink(query, origin, text = query) {
    const link = document.createElement('a');
    link.href = this.getSearchURL(query, origin);
    link.textContent = text;
    link.addEventListener('click', (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.navigateToQuery(query, origin);
    });
    return link;
  }

  createSearchGuidance(result) {
    const guidance = document.createElement('div');
    guidance.className = 'collection-plp__search-guidance';
    guidance.setAttribute('role', 'status');
    guidance.setAttribute('aria-live', 'polite');

    const correctedFrom = this.correctedFrom;
    if (correctedFrom) {
      const message = document.createElement('p');
      const template =
        correctedFrom.totalHits > 0
          ? this.getLabel('autocorrect-text-template', 'Showing results for “__corrected__”.')
          : this.getLabel(
              'autocorrect-no-hits-text-template',
              'No results found for “__original__”. Showing results for “__corrected__”.',
            );
      message.append(
        document.createTextNode(
          template.replace('__original__', correctedFrom.query).replace('__corrected__', this.query),
        ),
      );

      if (correctedFrom.totalHits > 0) {
        message.append(
          document.createTextNode(' '),
          this.createSearchLink(
            correctedFrom.query,
            'UNDO_AUTO_CORRECT',
            this.getLabel('undo-autocorrect-text-template', 'Search instead for “__original__”').replace(
              '__original__',
              correctedFrom.query,
            ),
          ),
        );
      }
      guidance.append(message);
    } else if (result.didYouMean?.length) {
      const message = document.createElement('p');
      message.append(document.createTextNode(`${this.getLabel('did-you-mean-text', 'Did you mean:')} `));
      result.didYouMean.forEach((suggestion, index) => {
        if (index) message.append(document.createTextNode(', '));
        message.append(this.createSearchLink(suggestion.q, 'DID_YOU_MEAN'));
      });
      guidance.append(message);
    }

    if (result.totalHits === 0) {
      const message = document.createElement('p');
      message.className = 'collection-plp__search-empty';
      message.textContent = this.query
        ? this.getLabel('no-results-text-template', 'No results found for “__query__”.').replace(
            '__query__',
            this.query,
          )
        : this.getLabel('empty-query-text', 'Enter a search term to find products.');
      guidance.append(message);
    }

    if (result.relatedSearches?.length) {
      const related = document.createElement('nav');
      related.className = 'collection-plp__related-searches';
      related.setAttribute('aria-label', this.getLabel('related-searches-text', 'Related searches'));

      const label = document.createElement('span');
      label.textContent = `${this.getLabel('related-searches-text', 'Related searches')}: `;
      related.append(label);
      result.relatedSearches.forEach((suggestion, index) => {
        if (index) related.append(document.createTextNode(', '));
        related.append(this.createSearchLink(suggestion.q, 'RELATED_SEARCHES'));
      });
      guidance.append(related);
    }

    return guidance.childElementCount ? guidance : null;
  }

  get correctedFrom() {
    const url = new URL(window.location.href);
    const query = url.searchParams.get(CORRECTED_FROM_PARAM);
    if (!query) return null;
    return { query, totalHits: Number(url.searchParams.get(CORRECTED_FROM_HITS_PARAM)) || 0 };
  }

  applyAutoCorrect(result) {
    if (!result.autoCorrect?.q) return;

    const url = new URL(window.location.href);
    url.searchParams.set(CORRECTED_FROM_PARAM, this.query);
    url.searchParams.set(CORRECTED_FROM_HITS_PARAM, String(result.autoCorrect.originalTotalHits ?? 0));
    url.searchParams.set('q', result.autoCorrect.q);
    history.replaceState(history.state, '', url);
    this.syncSearchInput();
  }

  render(result) {
    this.applyAutoCorrect(result);
    super.render(result);
    this.querySelectorAll('.collection-plp__search-guidance').forEach((element) => element.remove());
    const guidance = this.createSearchGuidance(result);
    const panel = this.querySelector('[data-collection-plp]');
    if (guidance && panel) panel.prepend(guidance);
  }

  handlePopState() {
    this.syncSearchInput();
    super.handlePopState();
  }
}

if (!customElements.get('search-plp')) {
  customElements.define('search-plp', SearchPlp);
}
